import { Field, FieldArray, Formik } from 'formik'
import type { FieldProps, FormikProps } from 'formik'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { DEFAULT_LOCALE } from '../../questions/questionFormOptions'
import type { CreateFormFormValues, SectionFormValues } from '../createFormModal/CreateFormModal.types'
import {
  HEX_COLOR_REGEX,
  QUESTION_TYPE_LABELS,
  getConditionalTargetIds,
  hasConditionalOptions
} from '../createFormModal/constants'
import { FormPreview } from '../createFormModal/FormPreview'
import { QuestionPickerModal } from '../createFormModal/QuestionPickerModal'
import modalStyles from '../createFormModal/CreateFormModal.module.scss'

import type { EditFormModalProps } from './EditFormModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  Accordion,
  AcknowledgeModal,
  Badge,
  ConfirmationModal,
  ButtonText,
  Button,
  Flex,
  FormField,
  Heading,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
  TextArea,
  TextInput
} from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'

const generateUid = () => crypto.randomUUID()

const createEmptySection = (): SectionFormValues => ({
  uid: generateUid(),
  name: '',
  description: '',
  questions: []
})

export function EditFormModal({ isOpen, onClose, refresh, form }: Readonly<EditFormModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const [modalState, setModalState] = useState<'confirm' | 'success' | 'error' | null>(null)
  const [questionPickerPageIndex, setQuestionPickerPageIndex] = useState<number | null>(null)
  const capturedValuesRef = useRef<CreateFormFormValues | null>(null)

  const { data: formDetails, isLoading: isLoadingDetails } = useQuery(
    ['/v1/admin/grants/forms', form?.id ?? ''],
    grantProgramService.getFormById,
    { formId: form?.id ?? '' },
    { enabled: Boolean(form?.id) && isOpen }
  )

  const [{ isPending }, { apiFunc: updateForm }] = useMutation(
    grantProgramService.updateForm,
    () => {
      refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const formikRef = useRef<FormikProps<CreateFormFormValues> | null>(null)

  const handleClose = useCallback(() => {
    formikRef.current?.resetForm()
    onClose()
    setModalState(null)
    setQuestionPickerPageIndex(null)
  }, [onClose])

  const initialValues = useMemo<CreateFormFormValues>(() => {
    if (!formDetails) {
      return {
        internalName: '',
        description: '',
        partnerId: '', // Required by shared type, not used in edit payload
        logo: '',
        primaryColor: '',
        secondaryColor: '',
        pages: [createEmptySection()]
      }
    }

    return {
      internalName: formDetails.internalName ?? '',
      description: formDetails.description ?? '',
      partnerId: '', // Required by shared type, not used in edit payload
      logo: formDetails.logo ?? '',
      primaryColor: formDetails.primaryColor ?? '',
      secondaryColor: formDetails.secondaryColor ?? '',
      pages:
        formDetails.pages.length > 0
          ? formDetails.pages.map(page => ({
              uid: generateUid(),
              name: page.title ?? '',
              description: page.description ?? '',
              questions: page.questions.map(q => ({
                uid: generateUid(),
                id: q.id,
                label: q.label,
                type: q.type,
                required: q.required,
                hidden: q.hidden,
                maxScore: q.maxScore,
                multiple: q.multiple,
                options: q.options
              }))
            }))
          : [createEmptySection()]
    }
  }, [formDetails])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        internalName: Yup.string().min(1).max(255).required(t('errorMessage.required')),
        description: Yup.string().optional(),
        logo: Yup.string().url(t('grants.forms.createModal.logo.invalid')).optional(),
        primaryColor: Yup.string().matches(HEX_COLOR_REGEX, t('grants.forms.createModal.color.invalid')).optional(),
        secondaryColor: Yup.string().matches(HEX_COLOR_REGEX, t('grants.forms.createModal.color.invalid')).optional(),
        pages: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string().min(1).required(t('errorMessage.required'))
            })
          )
          .min(1)
          .required()
      }),
    [t]
  )

  const buildPayload = useCallback(
    (values: CreateFormFormValues) => ({
      internalName: values.internalName,
      ...(values.description ? { description: { [DEFAULT_LOCALE]: values.description } } : {}),
      ...(values.logo ? { logo: values.logo } : {}),
      ...(values.primaryColor ? { primaryColor: values.primaryColor } : {}),
      ...(values.secondaryColor ? { secondaryColor: values.secondaryColor } : {}),
      pages: values.pages.map(page => ({
        title: { [DEFAULT_LOCALE]: page.name },
        ...(page.description ? { description: { [DEFAULT_LOCALE]: page.description } } : {}),
        ...(page.questions.length > 0 ? { questions: page.questions.map(q => ({ id: q.id })) } : {})
      }))
    }),
    []
  )

  const handleSubmit = useCallback(
    (values: CreateFormFormValues) => {
      if (!form?.id) return

      if (form.linkedTasksCount && form.linkedTasksCount > 0) {
        capturedValuesRef.current = values
        setModalState('confirm')

        return
      }

      updateForm({ formId: form.id, payload: buildPayload(values) })
    },
    [form?.id, form?.linkedTasksCount, buildPayload, updateForm]
  )

  const handleConfirmSubmit = useCallback(() => {
    if (!form?.id || !capturedValuesRef.current) return

    updateForm({ formId: form.id, payload: buildPayload(capturedValuesRef.current) })
    capturedValuesRef.current = null
  }, [form?.id, buildPayload, updateForm])

  if (modalState === 'confirm') {
    return (
      <Modal open={isOpen} onClose={() => setModalState(null)}>
        <ConfirmationModal
          title={t('grants.forms.editModal.confirm.title')}
          primaryButtonText={t('grants.forms.editModal.confirm.submit')}
          primaryBtnTestId="edit-form-confirm-button"
          secondaryButtonText={t('button.cancel')}
          handleSubmit={handleConfirmSubmit}
          handleClose={() => setModalState(null)}
          loading={isPending}
        >
          {t('grants.forms.editModal.confirm.description', {
            count: form?.linkedTasksCount ?? 0
          })}
        </ConfirmationModal>
      </Modal>
    )
  }

  if (modalState === 'success') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="positive"
          title={t('grants.forms.editModal.success.title')}
          description={t('grants.forms.editModal.success.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  if (modalState === 'error') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="negative"
          title={t('grants.forms.editModal.error.title')}
          description={t('grants.forms.editModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  if (isLoadingDetails) {
    return (
      <Modal open={isOpen} onClose={handleClose} width={1100}>
        <ModalContent>
          <Flex justify="center" align="center" py={48}>
            <Spinner size={8} />
          </Flex>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} width={1100} showModalOverflow={false}>
      <Formik<CreateFormFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {formik => {
          formikRef.current = formik

          return (
            <>
              <ModalHeader>
                <Heading level="h4">{t('grants.forms.editModal.title')}</Heading>
              </ModalHeader>
              <ModalContent maxHeight="60vh">
                <Flex gap="lg" className={modalStyles.contentLayout}>
                  <Flex direction="column" gap="lg" className={modalStyles.formColumn}>
                    <Heading level="h5">{t('grants.forms.createModal.details')}</Heading>

                    <Field name="internalName">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.name.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="edit-form-name"
                            placeholder={t('grants.forms.createModal.name.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="description">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.description.label')}
                          necessity="optional"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextArea
                            data-testid="edit-form-description"
                            placeholder={t('grants.forms.createModal.description.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                            className={modalStyles.descriptionTextarea}
                          />
                        </FormField>
                      )}
                    </Field>

                    {formDetails?.partner && (
                      <FormField label={t('grants.forms.createModal.partner.label')} necessity="optional">
                        <TextInput name="partnerId" value={formDetails.partner.name} disabled />
                      </FormField>
                    )}

                    <Field name="logo">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.logo.label')}
                          necessity="optional"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="edit-form-logo"
                            placeholder={t('grants.forms.createModal.logo.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Flex gap="md">
                      <Flex flex={1}>
                        <Field name="primaryColor">
                          {({ field, meta }: FieldProps<string>) => (
                            <FormField
                              label={t('grants.forms.createModal.primaryColor.label')}
                              necessity="optional"
                              status={meta.touched && meta.error ? 'danger' : 'default'}
                              statusMessage={meta.touched && meta.error ? meta.error : undefined}
                            >
                              <TextInput
                                data-testid="edit-form-primary-color"
                                placeholder="#FF0000"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                status={meta.touched && meta.error ? 'danger' : 'default'}
                              />
                            </FormField>
                          )}
                        </Field>
                      </Flex>
                      <Flex flex={1}>
                        <Field name="secondaryColor">
                          {({ field, meta }: FieldProps<string>) => (
                            <FormField
                              label={t('grants.forms.createModal.secondaryColor.label')}
                              necessity="optional"
                              status={meta.touched && meta.error ? 'danger' : 'default'}
                              statusMessage={meta.touched && meta.error ? meta.error : undefined}
                            >
                              <TextInput
                                data-testid="edit-form-secondary-color"
                                placeholder="#0000FF"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                status={meta.touched && meta.error ? 'danger' : 'default'}
                              />
                            </FormField>
                          )}
                        </Field>
                      </Flex>
                    </Flex>

                    <FieldArray name="pages">
                      {({ push, remove }) => (
                        <Flex direction="column" gap="md">
                          <Flex justify="space-between" align="center">
                            <Heading level="h5">{t('grants.forms.createModal.content')}</Heading>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => push(createEmptySection())}
                              data-testid="add-section-button"
                            >
                              {t('grants.forms.createModal.addSection')}
                            </Button>
                          </Flex>

                          <Accordion multiple defaultValue={formik.values.pages.map((_, i) => `section-${i}`)}>
                            {formik.values.pages.map((page, pageIndex) => (
                              <Accordion.Item key={page.uid} value={`section-${pageIndex}`}>
                                <Accordion.Control>
                                  <Flex justify="space-between" align="center">
                                    <Text>
                                      {page.name ||
                                        t('grants.forms.createModal.section.defaultTitle', { number: pageIndex + 1 })}
                                    </Text>
                                    <Text size="small" color="grey500">
                                      {t('grants.forms.createModal.section.questionCount', {
                                        count: page.questions.length
                                      })}
                                    </Text>
                                  </Flex>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <Flex direction="column" gap="md">
                                    <Field name={`pages.${pageIndex}.name`}>
                                      {({ field, meta }: FieldProps<string>) => (
                                        <FormField
                                          label={t('grants.forms.createModal.section.name.label')}
                                          necessity="required"
                                          status={meta.touched && meta.error ? 'danger' : 'default'}
                                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                                        >
                                          <TextInput
                                            data-testid={`section-name-${pageIndex}`}
                                            placeholder={t('grants.forms.createModal.section.name.placeholder')}
                                            name={field.name}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            status={meta.touched && meta.error ? 'danger' : 'default'}
                                          />
                                        </FormField>
                                      )}
                                    </Field>

                                    <Field name={`pages.${pageIndex}.description`}>
                                      {({ field }: FieldProps<string>) => (
                                        <FormField
                                          label={t('grants.forms.createModal.section.description.label')}
                                          necessity="optional"
                                        >
                                          <TextArea
                                            data-testid={`section-description-${pageIndex}`}
                                            placeholder={t('grants.forms.createModal.section.description.placeholder')}
                                            name={field.name}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            className={modalStyles.sectionDescriptionTextarea}
                                          />
                                        </FormField>
                                      )}
                                    </Field>

                                    <FieldArray name={`pages.${pageIndex}.questions`}>
                                      {({ push: pushQuestion, remove: removeQuestion }) => {
                                        const conditionalTargetIds = getConditionalTargetIds(page.questions)

                                        return (
                                          <FormField
                                            label={t('grants.forms.createModal.section.questions.label')}
                                            necessity="optional"
                                          >
                                            <Flex direction="column" gap="sm" w="100%">
                                              {page.questions.map((question, qIndex) => {
                                                const isConditionalTarget = conditionalTargetIds.has(question.id)

                                                return (
                                                  <Flex
                                                    key={question.uid}
                                                    gap="sm"
                                                    align="center"
                                                    py={12}
                                                    px={16}
                                                    className={modalStyles.questionCard}
                                                    style={
                                                      isConditionalTarget
                                                        ? { marginLeft: 16, borderLeft: '3px solid #6366f1' }
                                                        : undefined
                                                    }
                                                  >
                                                    <Flex flex={1}>
                                                      <Text size="small">{question.label}</Text>
                                                    </Flex>
                                                    {question.hidden && (
                                                      <Badge variant="default">
                                                        {t('grants.forms.createModal.section.questions.hidden')}
                                                      </Badge>
                                                    )}
                                                    {isConditionalTarget && (
                                                      <Badge variant="warning">
                                                        {t('grants.forms.createModal.section.questions.conditional')}
                                                      </Badge>
                                                    )}
                                                    {hasConditionalOptions(question) && (
                                                      <Badge variant="informative">
                                                        {t('grants.forms.createModal.section.questions.hasConditions')}
                                                      </Badge>
                                                    )}
                                                    <Badge variant="default">
                                                      {QUESTION_TYPE_LABELS[question.type] ?? question.type}
                                                    </Badge>
                                                    <Badge variant={question.required ? 'informative' : 'default'}>
                                                      {question.required
                                                        ? t('grants.forms.createModal.section.questions.required')
                                                        : t('grants.forms.createModal.section.questions.optional')}
                                                    </Badge>
                                                    <ButtonText
                                                      onClick={() => removeQuestion(qIndex)}
                                                      data-testid={`remove-question-${pageIndex}-${qIndex}`}
                                                      className={modalStyles.removeButton}
                                                    >
                                                      <Icon name="bin" size={5} color="gray400" />
                                                    </ButtonText>
                                                  </Flex>
                                                )
                                              })}
                                              <Button
                                                variant="tertiary"
                                                size="small"
                                                onClick={() => setQuestionPickerPageIndex(pageIndex)}
                                                data-testid={`add-question-to-section-${pageIndex}`}
                                              >
                                                {t('grants.forms.createModal.section.questions.add')}
                                              </Button>
                                              <QuestionPickerModal
                                                isOpen={questionPickerPageIndex === pageIndex}
                                                onClose={() => setQuestionPickerPageIndex(null)}
                                                onSelect={question => {
                                                  pushQuestion({
                                                    uid: generateUid(),
                                                    ...question
                                                  })
                                                }}
                                                excludedQuestionIds={new Set(page.questions.map(q => q.id))}
                                                partnerId={formDetails?.partner?.id ?? ''}
                                                partnerName={formDetails?.partner?.name ?? ''}
                                              />
                                            </Flex>
                                          </FormField>
                                        )
                                      }}
                                    </FieldArray>

                                    {formik.values.pages.length > 1 && (
                                      <Flex justify="flex-end">
                                        <Button
                                          variant="reject"
                                          size="small"
                                          onClick={() => remove(pageIndex)}
                                          data-testid={`remove-section-${pageIndex}`}
                                        >
                                          {t('grants.forms.createModal.section.remove')}
                                        </Button>
                                      </Flex>
                                    )}
                                  </Flex>
                                </Accordion.Panel>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </Flex>
                      )}
                    </FieldArray>
                  </Flex>
                  <Flex direction="column" className={modalStyles.previewPanel} data-testid="form-preview-panel">
                    <FormPreview values={formik.values} />
                  </Flex>
                </Flex>
              </ModalContent>
              <ModalFooter>
                <Flex gap="md" justify="flex-end">
                  <Button variant="secondary" onClick={handleClose} data-testid="edit-form-cancel-button">
                    {t('button.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={formik.submitForm}
                    loading={isPending}
                    disabled={!formik.isValid || !formik.dirty || isPending}
                    data-testid="edit-form-submit-button"
                  >
                    {t('button.save')}
                  </Button>
                </Flex>
              </ModalFooter>
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}
